import axios, { AxiosResponse } from 'axios'
import { AtendimentoDto, AtendimentoView } from '../../apps/atendimento/core/_models'
import { AuthTokenResponse, VideoChamadaToken } from '../../auth'
import {
  AceiteTermoDtoCreate,
  AvaliacaoAtendimentoDtoCreate,
  TermoConsentimentoPublic,
  TokenDtoAtendimento,
} from './_models'
import { axiosTeleConsulta } from './axiosTeleConsulta'
import qs from 'qs'

const API_URL = process.env.REACT_APP_API_URL

export const LOGIN_URL = `${API_URL}/auth/token`
const ATENDIMENTO_PUBLIC_URL = `${API_URL}/public/paciente/atendimento`
const TERMO_ATENDIMENTO_URL = `${API_URL}/public/paciente/termo/atendimento`
const ACEITE_TERMO_URL = `${API_URL}/public/paciente/aceite-termo`
const AVALIACAO_URL = `${API_URL}/public/paciente/avaliacao`

/**
 * Token para atendimento
 * @param token TokenDtoAtendimento
 * @returns AuthTokenResponse
 */
export const loginByToken = (token: TokenDtoAtendimento): Promise<AuthTokenResponse> => {
  return axiosTeleConsulta
    .post<AuthTokenResponse>(LOGIN_URL, token)
    .then((retorno: AxiosResponse<AuthTokenResponse>) => retorno.data)
}

/**
 * Busca o termo de consentimento vigente do atendimento
 * @param protocolo Protocolo do Atendimento (precisa ser o do token do paciente)
 * @returns TermoConsentimentoPublic
 */
export const getTermoByProtocolo = (protocolo: string): Promise<TermoConsentimentoPublic> => {
  return axiosTeleConsulta
    .get(`${TERMO_ATENDIMENTO_URL}/${protocolo}`)
    .then((response: AxiosResponse<TermoConsentimentoPublic>) => response.data)
}

/**
 * Registra o aceite do paciente ao termo de consentimento apresentado
 * @param data AceiteTermoDtoCreate
 */
export const registrarAceiteTermo = (data: AceiteTermoDtoCreate): Promise<void> => {
  return axiosTeleConsulta.post(ACEITE_TERMO_URL, data).then(() => undefined)
}

/**
 * Identifica o 403 do gate de aceite (`ACEITE_TERMO_PENDENTE`), que o backend devolve
 * quando o paciente ainda não registrou o aceite do termo do atendimento.
 * O `BadRequestResponse` embrulha o corpo em `data`, daí o `data.data.code`.
 */
export const isAceiteTermoPendente = (error: any): boolean =>
  error?.response?.status === 403 && error?.response?.data?.data?.code === 'ACEITE_TERMO_PENDENTE'

/**
 * Recusa da rota do token de vídeo (não participante): classifica pelo status, não pelo texto. O 403
 * `ATENDIMENTO_ENCERRADO` do link é fim de atendimento, não falta de acesso.
 */
export const isAcessoNegadoVideo = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403) &&
  error.response?.data?.data?.code !== 'ATENDIMENTO_ENCERRADO'

/** Token de vídeo do paciente do link: o atendimento e o uid saem do JWT que o `axiosTeleConsulta` envia. */
export const geraTokenVideoPaciente = (): Promise<VideoChamadaToken> => {
  return axiosTeleConsulta
    .get(`${API_URL}/public/paciente/video-chamada/token`)
    .then((response: AxiosResponse<VideoChamadaToken>) => response.data)
}

/**
 * Busca um atendimento
 * @param protocolo Protocolo do Atendimento
 * @returns AtendimentoView
 */
export const getAtendimentoByProtocolo = (
  protocolo: string
): Promise<AtendimentoView | undefined> => {
  return axiosTeleConsulta
    .get(`${ATENDIMENTO_PUBLIC_URL}/${protocolo}`)
    .then((response: AxiosResponse<AtendimentoView>) => response.data)
}

/**
 * Registra cancelamento de um atendimento pelo paciente, com o motivo tipado do catálogo.
 *
 * Código e detalhamento vão em parâmetros separados — sem concatenar o detalhe no rótulo.
 * @param protocolo Protocolo do Atendimento
 * @param motivoCodigo Código do catálogo (ex.: `PEDIDO_PACIENTE`)
 * @param motivoDetalhe Detalhamento livre; obrigatório quando o código é `OUTROS`
 * @returns AtendimentoDto
 */
export const cancelAtendimento = (
  protocolo: string,
  motivoCodigo: string,
  motivoDetalhe?: string
): Promise<AtendimentoView | undefined> => {
  return axiosTeleConsulta
    .delete(`${ATENDIMENTO_PUBLIC_URL}/${protocolo}`, {
      params: {
        ...(motivoCodigo ? { motivoCodigo } : {}),
        ...(motivoDetalhe ? { motivoDetalhe } : {}),
      },
      paramsSerializer: (params) => qs.stringify(params, { encode: true, format: 'RFC3986' }),
    })
    .then((response: AxiosResponse<AtendimentoView>) => response.data)
}

/**
 * Avalia atendimento - NPS
 * @param protocolo Protocolo do atendimento
 * @param data AvaliacaoAtendimentoDtoCreate
 * @returns AtendimentoDto
 */
export const avaliacaoNPSAtendimento = (
  protocolo: string,
  data: AvaliacaoAtendimentoDtoCreate
): Promise<AtendimentoDto> => {
  return axiosTeleConsulta
    .post(`${AVALIACAO_URL}/${protocolo}`, data)
    .then((response: AxiosResponse<AtendimentoDto>) => response.data)
}

export const addMensagemPublica = (
  protocolo: string,
  mensagem: string
): Promise<AtendimentoDto> => {
  return axiosTeleConsulta
    .post<AtendimentoDto>(`${API_URL}/public/paciente/mensagem/${protocolo}`, {
      mensagem,
    })
    .then((response: AxiosResponse<AtendimentoDto>) => response.data)
}
